import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewRef } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { CustomTranslationService } from 'src/app/service/language/custom-translation.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { PermissionsConstants } from 'src/app/service/myaccount/my-account-permission.constants';
import { MyAccountPermissionService } from 'src/app/service/myaccount/my-account-permission.service';
import { OrderDetailsService } from 'src/app/service/myaccount/order-details.service';

@Component({
  selector: 'app-order-details-invoice',
  templateUrl: './order-details-invoice.component.html',
  styleUrls: ['./order-details-invoice.component.scss'],
  standalone: false,
})
export class OrderDetailsInvoiceComponent implements OnInit, OnDestroy {
  orders$: Observable<any>;
  orderId: string;
  isCustomTranslationsEnabled: boolean;

  orderDetailsSubscription$: Subscription;
  isCustomTranslationsEnabledSubs$: Subscription;
  // custom Order type
  order: any;
  showDesktop = true;

  activeCountry$: Observable<string> = this.countryService.getActive();
  activeLanguage$: Observable<string> = this.languageService.getActive();

  isDownloadInvoiceEnabled$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.DOWNLOAD_INVOICE)
    )
  );

  myAccountPermissionSubscription: Subscription;
  showInvoice: boolean;
  features: any = {};

  constructor(
    protected orderDetailsService: OrderDetailsService,
    private componentService: ComponentService,
    protected deviceService: DeviceDetectorService,
    private cdr: ChangeDetectorRef,
    private checkoutServiceExtends: CheckoutServiceExtends,
    private countryService: ShimanoCountryService,
    private languageService: ShimanoLanguageService,
    private customTranslationService: CustomTranslationService,
    private myAccountPermissionService: MyAccountPermissionService
  ) {
    if (!this.deviceService.isDesktop()) {
      this.showDesktop = false;
    }
  }

  ngOnInit() {
    const orderDetailsAPI = this.componentService.orderDetailsEntries$.getValue();
    this.order = orderDetailsAPI;
    this.setIsCustomTranslationsEnabled();
    // It will trigger this once cancel-edit order
    this.orderDetailsSubscription$ = this.componentService.orderDetails$.subscribe((orderDetails) => {
      this.order = orderDetails;
      this.addFeatures(orderDetails);
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });
    this.myAccountPermissionSubscription = this.myAccountPermissionService.currentUserPermissions$.subscribe(
      (permissions) => {
        this.showInvoice = permissions ? permissions.includes(PermissionsConstants.VIEW_AND_PAY_INVOICES) : true;
        this.cdr.detectChanges();
      }
    );
  }

  setIsCustomTranslationsEnabled(): void {
    this.isCustomTranslationsEnabledSubs$ = combineLatest([this.activeCountry$, this.activeLanguage$])
      .pipe(
        switchMap(([activeCountry, activeLanguage]) =>
          this.customTranslationService.isCustomTranslationsEnabled$(activeCountry, activeLanguage, [
            'myAccount.orderDetails.invoiceSummary.empty',
          ])
        )
      )
      .subscribe((data) => {
        this.isCustomTranslationsEnabled = data;
        this.cdr.detectChanges();
      });
  }

  filterInvoice(consignments: any[]): any[] {
    const seenReferenceNumbers = new Set();
    return consignments.filter((item) => {
      const referenceNumber = item.invoice?.referenceNumber;
      if (referenceNumber && !seenReferenceNumbers.has(referenceNumber)) {
        seenReferenceNumbers.add(referenceNumber);
        return true;
      }
      return false;
    });
  }

  filterValidInvoice(consignments: any[]): any[] {
    return consignments.filter((i) => i.invoice && i.invoice.downloadLink !== '');
  }

  downloadAllInvoices(invoices: any[]): void {
    invoices.forEach((link) => {
      const prop = this.getNested(link, 'invoice', 'downloadLink');
      if (typeof prop !== 'undefined' && link.invoice.downloadLink !== '') {
        window.open(link.invoice.downloadLink, '_blank');
      }
    });
  }

  getNested(obj: any, ...args: string[]) {
    // tslint:disable-next-line: no-shadowed-variable
    return args.reduce((obj, level) => obj && obj[level], obj);
  }

  setCustomContext(isCustomTranslationsEnabled: boolean, activeCountry: string, activeLanguage: string) {
    return isCustomTranslationsEnabled ? [activeCountry, activeLanguage].join('-') : null;
  }

  addFeatures(order: any): void {
    const orderType = order.orderTypeCode;

    this.features.hidePayment = ['CO', 'SW'].includes(orderType);
  }

  ngOnDestroy() {
    if (this.orderDetailsSubscription$) {
      this.orderDetailsSubscription$.unsubscribe();
    }
    this.orderDetailsSubscription$?.unsubscribe();
  }
}
